import { LedgerEntry } from '@dataTypes/billing.types'
import { Discount } from '@dataTypes/discount.type'
import {
  createInvoice,
  fetchInvoiceEntries,
  setInvoicePagination,
} from '@store/actions/billing.action'
import { getAllDiscount } from '@store/actions/discount.action'
import { AppDispatch, RootState } from '@store/index'
import { getLedgerEntries } from '@services/api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { logger } from '@utils/safeLogger'
interface UseCreateInvoiceModalReturn {
  selectedDiscountId: string
  customDiscount: number
  selectedAppointmentKey: string
  selectedLedgerEntries: LedgerEntry[]
  invoicePatientName: string
  discounts: Discount[]
  availableLedgerEntries: LedgerEntry[]
  discountPercent: number
  totals: {
    totalCharges: number
    totalApprovedInsurance: number
    totalDiscount: number
    totalAppointmentAdjustment: number
    totalOutstanding: number
  }
  setSelectedDiscountId: (value: string) => void
  setCustomDiscount: (value: number) => void
  setSelectedAppointmentKey: (value: string) => void
  handleSelectAppointment: (appointmentId: string) => void
  handleDelete: (appointmentId: string) => void
  handleCreateInvoice: () => Promise<void>
}

const useCreateInvoiceModal = (
  isVisible: boolean,
  onClose: () => void,
  ledgerEntries: LedgerEntry[],
  initialSelectedEntry: LedgerEntry | null,
  showLoading: (options: {
    container: string
    hideElements?: string[]
  }) => void,
  hideLoading: (container: string) => void,
  showToast: (toast: ReturnType<typeof createSuccessToast>) => void,
): UseCreateInvoiceModalReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const { discounts } = useSelector((state: RootState) => state.discount)
  const selectedPatient = useSelector(
    (state: RootState) => state.patient.selectedPatient,
  )
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )

  const [selectedDiscountId, setSelectedDiscountId] = useState<string>('')
  const [customDiscount, setCustomDiscount] = useState<number>(0)
  const [selectedAppointmentKey, setSelectedAppointmentKey] =
    useState<string>('')
  const [selectedLedgerEntries, setSelectedLedgerEntries] = useState<
    LedgerEntry[]
  >([])
  const [invoicePatientName, setInvoicePatientName] = useState<string>('')
  const [fetchedLedgerEntries, setFetchedLedgerEntries] = useState<
    LedgerEntry[]
  >([])

  const discountInitializedRef = useRef(false)
  const initialEntryProcessedRef = useRef(false)

  useEffect(() => {
    if (!isVisible) return

    const fetchDiscounts = async (): Promise<void> => {
      try {
        await dispatch(getAllDiscount()).unwrap()
        discountInitializedRef.current = false
        initialEntryProcessedRef.current = false
      } catch (error) {
        logger.error('Error fetching discount settings:', error)
      }
    }

    fetchDiscounts()
  }, [dispatch, isVisible])

  useEffect(() => {
    if (!isVisible) {
      setFetchedLedgerEntries([])
      return
    }

    const fetchAllLedgerEntries = async (): Promise<void> => {
      try {
        const filters: {
          patient_id?: string
          clinic_id?: string
        } = {}

        if (selectedPatient?.patient_id) {
          filters.patient_id = selectedPatient.patient_id
        }

        if (selectedClinic?.clinic_id) {
          filters.clinic_id = selectedClinic?.clinic_id
        }

        const response = await getLedgerEntries(
          Object.keys(filters).length > 0 ? filters : undefined,
        )

        if (response.statusCode === 200 && response.data?.entries) {
          setFetchedLedgerEntries(response.data.entries)
        } else {
          logger.error('Failed to fetch ledger entries:', response.message)
          setFetchedLedgerEntries([])
        }
      } catch (error) {
        logger.error('Error fetching ledger entries:', error)
        setFetchedLedgerEntries([])
      }
    }

    fetchAllLedgerEntries()
  }, [isVisible, selectedPatient?.patient_id, selectedClinic?.clinic_id])

  useEffect(() => {
    if (!isVisible || discountInitializedRef.current) return
    setSelectedDiscountId('')
    setCustomDiscount(0)
    discountInitializedRef.current = true
  }, [isVisible])

  useEffect(() => {
    if (
      isVisible &&
      initialSelectedEntry &&
      !initialEntryProcessedRef.current
    ) {
      setSelectedLedgerEntries([initialSelectedEntry])
      setInvoicePatientName(initialSelectedEntry.patientName)
      initialEntryProcessedRef.current = true
    }
  }, [isVisible, initialSelectedEntry])

  useEffect(() => {
    if (!isVisible) {
      setSelectedLedgerEntries([])
      setInvoicePatientName('')
      setSelectedAppointmentKey('')
      setSelectedDiscountId('')
      setCustomDiscount(0)
      discountInitializedRef.current = false
      initialEntryProcessedRef.current = false
    }
  }, [isVisible])

  // Use fetched ledger entries instead of prop (which is paginated)
  const allLedgerEntries = useMemo(() => {
    return fetchedLedgerEntries.length > 0
      ? fetchedLedgerEntries
      : ledgerEntries
  }, [fetchedLedgerEntries, ledgerEntries])

  const availableLedgerEntries = useMemo(() => {
    const selectedIds = new Set(
      selectedLedgerEntries.map((e) => e.appointmentId),
    )
    return allLedgerEntries.filter((entry) => {
      if (entry.invoiceNumber) return false
      if (selectedIds.has(entry.appointmentId)) return false
      if (invoicePatientName && entry.patientName !== invoicePatientName) {
        return false
      }
      return true
    })
  }, [allLedgerEntries, selectedLedgerEntries, invoicePatientName])

  const handleSelectAppointment = useCallback(
    (appointmentId: string): void => {
      const selectedEntry = allLedgerEntries.find(
        (entry) => entry.appointmentId === appointmentId,
      )
      if (!selectedEntry) return

      if (!invoicePatientName) {
        setInvoicePatientName(selectedEntry.patientName)
      }

      setSelectedLedgerEntries((prev) => [...prev, selectedEntry])
      setSelectedAppointmentKey('')
    },
    [allLedgerEntries, invoicePatientName],
  )

  const handleDelete = useCallback(
    (appointmentId: string): void => {
      const updatedEntries = selectedLedgerEntries.filter(
        (entry) => entry.appointmentId !== appointmentId,
      )
      setSelectedLedgerEntries(updatedEntries)

      if (updatedEntries.length === 0) {
        setInvoicePatientName('')
      }
    },
    [selectedLedgerEntries],
  )

  const discountPercent = useMemo(() => {
    if (selectedDiscountId === '') {
      return 0
    }
    if (selectedDiscountId === '-1') {
      return Number(customDiscount) || 0
    }
    const selectedDiscount = discounts.find(
      (setting) => setting.discount_id === selectedDiscountId,
    )
    return selectedDiscount?.discount_percentage ?? 0
  }, [selectedDiscountId, customDiscount, discounts])

  const totals = useMemo(() => {
    const charges = selectedLedgerEntries.reduce(
      (sum, entry) => sum + entry.clinicFee,
      0,
    )

    const patientResponsibility = selectedLedgerEntries.reduce(
      (sum, entry) => sum + entry.patientResponsibility,
      0,
    )

    const approvedInsurance = selectedLedgerEntries.reduce(
      (sum, entry) => sum + (entry.approvedInsurance ?? 0),
      0,
    )
    const discount = (patientResponsibility * discountPercent) / 100
    const appointmentAdjustment = selectedLedgerEntries.reduce(
      (sum, entry) => sum + (entry.appointmentAdjustment ?? 0),
      0,
    )
    const outstanding = patientResponsibility - discount - appointmentAdjustment

    return {
      totalCharges: charges,
      totalApprovedInsurance: approvedInsurance,
      totalDiscount: discount,
      totalAppointmentAdjustment: appointmentAdjustment,
      totalOutstanding: outstanding,
    }
  }, [selectedLedgerEntries, discountPercent])

  const handleCreateInvoice = useCallback(async (): Promise<void> => {
    if (selectedLedgerEntries.length === 0) {
      return
    }

    if (discountPercent < 0 || discountPercent > 100) {
      console.error('Discount percentage must be between 0 and 100')
      return
    }

    showLoading({
      container: '.ledger-table',
    })

    try {
      await dispatch(
        createInvoice({
          appointmentIds: selectedLedgerEntries.map(
            (entry) => entry.appointmentId,
          ),
          discountPercentage: discountPercent,
        }),
      ).unwrap()

      dispatch(setInvoicePagination({ currentPage: 1 }))
      await dispatch(fetchInvoiceEntries()).unwrap()

      hideLoading('.ledger-table')
      onClose()
      showToast(
        createSuccessToast(
          'Invoice created successfully',
          'Invoice has been created successfully.',
        ),
      )
    } catch (error) {
      logger.error('Failed to create invoice entry:', error)
      hideLoading('.ledger-table')
      showToast(
        createFailedToast(
          'Invoice not created',
          'Invoice not created. Please try again.',
        ),
      )
    }
  }, [
    selectedLedgerEntries,
    discountPercent,
    dispatch,
    onClose,
    showLoading,
    hideLoading,
    showToast,
  ])

  return {
    selectedDiscountId,
    customDiscount,
    selectedAppointmentKey,
    selectedLedgerEntries,
    invoicePatientName,
    discounts,
    availableLedgerEntries,
    discountPercent,
    totals,
    setSelectedDiscountId,
    setCustomDiscount,
    setSelectedAppointmentKey,
    handleSelectAppointment,
    handleDelete,
    handleCreateInvoice,
  }
}

export default useCreateInvoiceModal
